/** Steady-state gap between polls when the feed is drained. */
const POLL_IDLE_MS = 1_000;
/**
 * Gap used while draining a backlog (`hasMore`). Deliberately non-zero: a zero
 * delay turns a large backlog into an unthrottled request loop, and each poll
 * costs a folder-visibility scan on the server.
 */
const POLL_DRAIN_MS = 50;
const POLL_BACKOFF_BASE_MS = 2_000;
const POLL_BACKOFF_MAX_MS = 30_000;
/**
 * How many times the same cursor may fail to acknowledge before it is advanced
 * anyway. Replaying a failed cursor is what makes an invalidation durable, but
 * an event that can never apply (e.g. a refetch that always rejects) would
 * otherwise pin the cursor and re-poll forever. The local cache purge has
 * already happened by then; only the opportunistic refetch was lost, and
 * TanStack refetches those on the next mount/focus.
 */
const MAX_CURSOR_STALL_ATTEMPTS = 5;

export type PollOutcome =
  /** Feed drained; nothing left to fetch. */
  | 'applied'
  /** Batch applied but the server reports a backlog. */
  | 'applied-with-backlog'
  /** Events arrived but at least one failed to apply locally. */
  | 'apply-failed'
  | 'unauthorized'
  | 'request-failed';

export interface PollBackoffState {
  readonly consecutiveFailures: number;
  readonly cursorStallAttempts: number;
}

export interface PollBackoffDecision extends PollBackoffState {
  readonly delayMs: number;
  /**
   * Whether the cursor may advance past this batch. False keeps the batch
   * durable for replay; true gives up on it (see MAX_CURSOR_STALL_ATTEMPTS).
   */
  readonly acknowledgeCursor: boolean;
}

export const INITIAL_POLL_BACKOFF: PollBackoffState = {
  consecutiveFailures: 0,
  cursorStallAttempts: 0,
};

/**
 * Pure transition for the poll loop's pacing. Extracted so the pathological
 * cases — an unbounded backlog and a batch that can never apply — are testable
 * without driving the effect.
 */
export function nextPollBackoff(
  state: PollBackoffState,
  outcome: PollOutcome,
): PollBackoffDecision {
  switch (outcome) {
    case 'applied':
    case 'applied-with-backlog':
      return {
        ...INITIAL_POLL_BACKOFF,
        delayMs: outcome === 'applied-with-backlog' ? POLL_DRAIN_MS : POLL_IDLE_MS,
        acknowledgeCursor: true,
      };
    case 'unauthorized':
      return {
        consecutiveFailures: 0,
        cursorStallAttempts: state.cursorStallAttempts,
        delayMs: 5_000,
        acknowledgeCursor: false,
      };
    case 'apply-failed': {
      const consecutiveFailures = state.consecutiveFailures + 1;
      const stalled = state.cursorStallAttempts + 1;
      const giveUp = stalled >= MAX_CURSOR_STALL_ATTEMPTS;
      return {
        consecutiveFailures,
        cursorStallAttempts: giveUp ? 0 : stalled,
        delayMs: backoffDelay(consecutiveFailures),
        acknowledgeCursor: giveUp,
      };
    }
    case 'request-failed':
    default: {
      const consecutiveFailures = state.consecutiveFailures + 1;
      return {
        consecutiveFailures,
        cursorStallAttempts: state.cursorStallAttempts,
        delayMs: backoffDelay(consecutiveFailures),
        acknowledgeCursor: false,
      };
    }
  }
}

function backoffDelay(consecutiveFailures: number): number {
  const scaled = POLL_BACKOFF_BASE_MS * 2 ** Math.max(0, consecutiveFailures - 1);
  return Math.min(scaled, POLL_BACKOFF_MAX_MS);
}
